import { db } from '../firebase';
import { collection, doc, getDocs, getDoc } from 'firebase/firestore';

// Lightweight in-memory cache to avoid refetching on each tab revisit
let _usersCache = { items: null, ts: 0 };
const CACHE_TTL_MS = 60 * 1000; // 60 seconds

// Fetch aggregated users data for admin panel via direct Firestore reads
export async function fetchAdminUsers() {
  const usersSnap = await getDocs(collection(db, 'users'));
  const items = await Promise.all(
    usersSnap.docs.map(async (d) => {
      const uid = d.id;
      const root = d.data() || {};

      const name = root.name || '';
      const email = root.email || '';
      const role = root.role || 'user';
      const createdAt = root.createdAt || null;
      const premium = root.premium || null;
      const fcmTokens = Array.isArray(root.fcmTokens)
        ? root.fcmTokens.filter((t) => typeof t === 'string' && t.length > 0)
        : [];
      const fcmTokensCount = fcmTokens.length;

      // prefs_stats: onboarding.notification and stats.streak
      let notificationsMuted = false;
      let streakCount;
      let streakLastDay;
      let longestStreak;
      let badgesCount = 0;
      let favoritesCount = 0;
      try {
        const prefsRef = doc(db, 'users', uid, 'sync', 'prefs_stats');
        const prefsSnap = await getDoc(prefsRef);
        if (prefsSnap.exists()) {
          const data = prefsSnap.data() || {};
          const preferences = data.preferences || {};
          const onboarding = preferences.onboarding || {};
          notificationsMuted = onboarding.notification === 'silent';
          const stats = data.stats || {};
          const streak = stats.streak || {};
          if (Number.isFinite(streak.count)) streakCount = streak.count;
          if (typeof streak.lastDay === 'string') streakLastDay = streak.lastDay;

          // Longest streak: strictly use the stored value
          if (Number.isFinite(streak.longest)) longestStreak = streak.longest;

          // Fallback: derive badges count from stats.badges if present
          const badges = stats.badges;
          if (Array.isArray(badges)) {
            badgesCount = badges.length;
          } else if (badges && typeof badges === 'object') {
            badgesCount = Object.values(badges).reduce((sum, v) => {
              if (Array.isArray(v)) return sum + v.length;
              if (typeof v === 'number' && Number.isFinite(v)) return sum + v;
              return sum + 0;
            }, 0);
          }
        }
      } catch (_) {}

      // Prefer dedicated badges doc: users/{uid}/sync/badges
      try {
        const badgesRef = doc(db, 'users', uid, 'sync', 'badges');
        const badgesSnap = await getDoc(badgesRef);
        if (badgesSnap.exists()) {
          const b = badgesSnap.data() || {};
          if (Number.isFinite(b.earnedCount)) {
            badgesCount = b.earnedCount;
          } else if (b.earnedDates && typeof b.earnedDates === 'object') {
            badgesCount = Object.keys(b.earnedDates).length;
          }
        }
      } catch (_) {}

      // Favorites count: users/{uid}/sync/favorites
      try {
        const favRef = doc(db, 'users', uid, 'sync', 'favorites');
        const favSnap = await getDoc(favRef);
        if (favSnap.exists()) {
          const f = favSnap.data() || {};
          const ids = Array.isArray(f.taskIds)
            ? f.taskIds
            : Array.isArray(f.taskIDs)
              ? f.taskIDs
              : [];
          const cleaned = ids.filter((id) => typeof id === 'string' && id.length > 0);
          if (cleaned.length > 0) {
            favoritesCount = cleaned.length;
          } else if (Number.isFinite(f.count)) {
            favoritesCount = f.count;
          } else if (f.tasks && typeof f.tasks === 'object') {
            favoritesCount = Object.keys(f.tasks).length;
          }
        }
      } catch (_) {}

      // Counts
      let ownTasksCount = 0;
      let collectionsCount = 0;
      try {
        const ownSnap = await getDocs(collection(db, 'users', uid, 'ownTasks'));
        ownTasksCount = ownSnap.size;
      } catch (_) {}
      try {
        const colSnap = await getDocs(collection(db, 'users', uid, 'collections'));
        collectionsCount = colSnap.size;
      } catch (_) {}

      return {
        id: uid,
        uid,
        name,
        email,
        role,
        createdAt,
        premium,
        fcmTokensCount,
        notificationsMuted,
        streakCount,
        streakLastDay,
        longestStreak,
        badgesCount,
        favoritesCount,
        ownTasksCount,
        collectionsCount,
      };
    })
  );

  // Sort by createdAt desc when available
  items.sort((a, b) => {
    const ta = a.createdAt && a.createdAt.toMillis ? a.createdAt.toMillis() : a.createdAt || 0;
    const tb = b.createdAt && b.createdAt.toMillis ? b.createdAt.toMillis() : b.createdAt || 0;
    return tb - ta;
  });

  return items;
}

// Cached variant: returns cached results if within TTL; else refetches
export async function fetchAdminUsersCached() {
  const now = Date.now();
  if (_usersCache.items && (now - _usersCache.ts) < CACHE_TTL_MS) {
    return _usersCache.items;
  }
  const items = await fetchAdminUsers();
  _usersCache = { items, ts: Date.now() };
  return items;
}